import { Signal, computed } from "@angular/core";
import { ApprovalStatusType } from "../../domain/model/approval-status-type";
import { ApprovalStatusTypeEnum, WeighingTypeEnum } from "../../domain/model/model-enums";
import { StringUuid } from "../../domain/model/uuid/string-uuid";
import { getBatchWeighingsForSectionAndTrial } from "../../functions/data-store/data-store-batch-weighing.function";
import { getById, recordGet } from "../../functions/record.function";
import { StoreBatch } from "../../storage/data-store/model/master-data/store-batch";
import { StorePen } from "../../storage/data-store/model/master-data/store-pen";
import { StoreSection } from "../../storage/data-store/model/master-data/store-section";
import { StoreTrial } from "../../storage/data-store/model/master-data/store-trial";
import { StoreTrialPhase } from "../../storage/data-store/model/master-data/store-trial-phase";
import { StoreBatchWeighing } from "../../storage/data-store/model/registrations/store-batch-weighing";
import { TrialOverviewWeighingWeightViewModel } from "../trials/trial-overview-weighing-weight.viewmodel";
import { TrialOverviewWeighingDateViewModel } from "../trials/trial-overview-weighing-date.viewmodel";
import { TrialOverviewViewModel } from "../trials/trial-overview.viewmodel";
import { getPensForSectionAndTrial } from "../../functions/data-store/data-store-pen.function";
import { StoreApprovalStatus } from "../../storage/data-store/model/registrations/store-approval-status";
import { SectionViewModel } from "../shared/section.viewmodel";
import { getFinalTrialPhase, getFirstInterimTrialPhase, getSecondInterimTrialPhase } from "../../functions/data-store/data-store-trial.function";

export const trialOverviewRowViewModelsConst = (
  batchWeighings: Signal<Record<string, StoreBatchWeighing>>,
  sections: Signal<Record<string, StoreSection>>,
  trials: Signal<Record<string, StoreTrial>>,
  pens: Signal<Record<string, StorePen>>,
  batches: Signal<Record<string, StoreBatch>>,
) => {
   return computed(() => {
     const retValue: TrialOverviewViewModel[] = [];

     console.debug("trials: ", JSON.stringify(trials()));
     for (const [, trial] of Object.entries(trials())) {
        console.debug(`inspecting sections for trial id: ${trial.id}, name: ${trial.name}`);

        for (const sectionId of trial.sectionsIds) {
          const section = recordGet(sections(), sectionId.toString());
          const batchWeighingsResult = addTrialOverviewRowsUsingSection(trial, section, batchWeighings, batches);
          const pensForSection = getPensForSectionAndTrial(section.id, trial, pens);
          const pensName = getPensName(pensForSection);
          const sectionName = section.name;
          const trialId = new StringUuid(trial.id);
          const batchesName = getBatchesName(trial.id, sectionId, batchWeighings, batches);
          const trialOverviewRowVM = new TrialOverviewViewModel(trialId, trial.name, pensName, batchesName,
            //section
            new SectionViewModel(new StringUuid(section.id), sectionName!),
            //initial weighing
            batchWeighingsResult.initialVM!,

            //interim1 weighing
            batchWeighingsResult.interim1VM!,

            //interim2 weighing
            batchWeighingsResult.interim2VM!,

            //final weighing
            batchWeighingsResult.finalVM!
          );

          retValue.push(trialOverviewRowVM);
        }
    }
    //console.debug(`retValue: ${JSON.stringify(retValue)}`);
    return retValue;
  }
 );
};

function getBatchesName(
  trialId: string,
  sectionId: string,
  batchWeighings: Signal<Record<string, StoreBatchWeighing>>,
  batches: Signal<Record<string, StoreBatch>>,
) :string {
  const batchesForTrialAndSection = new Set<number>();
  for (const [, bw] of Object.entries(batchWeighings())) {
    const batchNumber = getById(batches, bw.batchId).number;
    if (bw.sectionId === sectionId && bw.trialId === trialId && !batchesForTrialAndSection.has(batchNumber)) {
      batchesForTrialAndSection.add(batchNumber);
    }
  }
  const batchesName = getCombinedNameforNumberSequence(Array.from(batchesForTrialAndSection.keys()));
  return batchesName;
}

function addTrialOverviewRowsUsingSection(
  trial: StoreTrial,
  section: StoreSection,
  batchWeighings: Signal<Record<string, StoreBatchWeighing>>,
  batches: Signal<Record<string, StoreBatch>>,
  ) :  {
      initialVM: TrialOverviewWeighingDateViewModel | undefined;
      interim1VM: TrialOverviewWeighingWeightViewModel | TrialOverviewWeighingDateViewModel | undefined;
      interim2VM: TrialOverviewWeighingWeightViewModel | TrialOverviewWeighingDateViewModel | undefined;
      finalVM: TrialOverviewWeighingWeightViewModel | TrialOverviewWeighingDateViewModel | undefined; } {

  const sectionName = section.name;
  console.debug(`  inspecting section, id: ${section.id}, name: ${section.name}`);

  let batchWeighingsResult: {
    initialVM: TrialOverviewWeighingDateViewModel | undefined;
    interim1VM: TrialOverviewWeighingWeightViewModel | TrialOverviewWeighingDateViewModel | undefined;
    interim2VM: TrialOverviewWeighingWeightViewModel | TrialOverviewWeighingDateViewModel | undefined;
    finalVM: TrialOverviewWeighingWeightViewModel | TrialOverviewWeighingDateViewModel | undefined; } = {
    initialVM: undefined,
    interim1VM: undefined,
    interim2VM: undefined,
    finalVM: undefined
  };

  batchWeighingsResult =
    getBatchWeighings(
      batchWeighings,
      section,
      trial,
      batches,
      sectionName);

  if (!batchWeighingsResult.initialVM) {
    batchWeighingsResult.initialVM = new TrialOverviewWeighingDateViewModel(new ApprovalStatusType(ApprovalStatusTypeEnum.NotApplicable.toString()));
  }

  if (!batchWeighingsResult.interim1VM) {
    batchWeighingsResult.interim1VM = GetOverviewWeighingBasedOnTrialPhaseInterim1(trial);
  }

  if (!batchWeighingsResult.interim2VM) {
    batchWeighingsResult.interim2VM = GetOverviewWeighingBasedOnTrialPhaseInterim2(trial);
  }

  if (!batchWeighingsResult.finalVM) {
    batchWeighingsResult.finalVM = GetOverviewWeighingBasedOnTrialPhaseFinal(trial);
  }

  return batchWeighingsResult;
}

function GetOverviewWeighingBasedOnTrialPhase(
  trialPhase: StoreTrialPhase | null) : TrialOverviewWeighingWeightViewModel | TrialOverviewWeighingDateViewModel {

  if (trialPhase) {
    if (trialPhase?.expectedStartDate) {
      return new TrialOverviewWeighingDateViewModel(
        new ApprovalStatusType(ApprovalStatusTypeEnum.Unknown.toString()), trialPhase?.expectedStartDate);
    }
    else if (trialPhase?.expectedEndWeightKg) {
      return new TrialOverviewWeighingWeightViewModel(
        new ApprovalStatusType(ApprovalStatusTypeEnum.Unknown.toString()), trialPhase?.expectedEndWeightKg);
    }
  }
  console.warn(`could not find trialPhase`);
  return new TrialOverviewWeighingDateViewModel(
    new ApprovalStatusType(ApprovalStatusTypeEnum.Unknown.toString()), null);
}

function GetOverviewWeighingBasedOnTrialPhaseFinal(trial: StoreTrial) : TrialOverviewWeighingWeightViewModel | TrialOverviewWeighingDateViewModel {
  const finalTrialPhase = getFinalTrialPhase(trial);
  if (finalTrialPhase) {
    console.debug(`found final trialPhase`);
  }

  return GetOverviewWeighingBasedOnTrialPhase(finalTrialPhase);
}

function GetOverviewWeighingBasedOnTrialPhaseInterim2(trial: StoreTrial) : TrialOverviewWeighingWeightViewModel | TrialOverviewWeighingDateViewModel {
  const secondTrialPhase = getSecondInterimTrialPhase(trial);
  if (secondTrialPhase) {
    console.debug(`found second interim trialPhase`);
  }
  return GetOverviewWeighingBasedOnTrialPhase(secondTrialPhase);
}

function GetOverviewWeighingBasedOnTrialPhaseInterim1(trial: StoreTrial) : TrialOverviewWeighingWeightViewModel | TrialOverviewWeighingDateViewModel {
  const firstTrialPhase = getFirstInterimTrialPhase(trial);
  if (firstTrialPhase) {
    console.debug(`found first interim trialPhase`);
  }
  return GetOverviewWeighingBasedOnTrialPhase(firstTrialPhase);
}



function getPensName(pensForSection: StorePen[]) {
  return getCombinedNameforNumberSequence (pensForSection.map((pen) => pen.number));
}

function getCombinedNameforNumberSequence(numbers: Array<number>) : string {

  const sortedNumbers = numbers.sort((n1,n2) => n1 - n2);

  if (sortedNumbers.length === 0) {
      return "";
  }

  if (sortedNumbers.length === 1) {
      return String (sortedNumbers[0]);
  }

  let name = "";
  let sequence = "";
  let currentValue = 0;
  let numbersInSeq = 0;
  const startSeparator = ""; //"[";
  const endSeparator = "";   //"]";
  for (let i = 0; i<sortedNumbers.length ; i++) {
    const emptySeq = sequence.length === 0;
        currentValue = sortedNumbers[i]!;

        if (emptySeq) {
            sequence = `${startSeparator}${currentValue}-`;
        }

        let prevValue = 0;
        if (i != 0) {
            prevValue = sortedNumbers[i-1]!;
        }

        if (currentValue - prevValue === 1 || i === 0) {
            numbersInSeq++;
            continue;
        }

        if (numbersInSeq === 1) {
            sequence = `${prevValue}`;
        }
        else {

          sequence += `${prevValue}${endSeparator}`;
        }

        name += sequence;
        if (i < sortedNumbers.length)
        {
            name += ", ";
        }

        sequence = `${startSeparator}${currentValue}-`;
        numbersInSeq = 1;
    }

    if (numbersInSeq === 1) {
        name += `${currentValue}`;
    }
    else {
        name += `${sequence}${currentValue}${endSeparator}`;
    }

    return name;
}

function getBatchWeighings
  (
    batchWeighings: Signal<Record<string, StoreBatchWeighing>>,
    section: StoreSection,
    trial: StoreTrial,
    batches: Signal<Record<string, StoreBatch>>,
    sectionName: string) : {
      initialVM: TrialOverviewWeighingDateViewModel | undefined;
      interim1VM: TrialOverviewWeighingWeightViewModel | TrialOverviewWeighingDateViewModel | undefined;
      interim2VM: TrialOverviewWeighingWeightViewModel | TrialOverviewWeighingDateViewModel | undefined;
      finalVM: TrialOverviewWeighingWeightViewModel | TrialOverviewWeighingDateViewModel | undefined; } {

  let initialVM: TrialOverviewWeighingDateViewModel | undefined = undefined;
  let interim1VM: TrialOverviewWeighingWeightViewModel | TrialOverviewWeighingDateViewModel | undefined = undefined;
  let interim2VM: TrialOverviewWeighingWeightViewModel | TrialOverviewWeighingDateViewModel | undefined = undefined;
  let finalVM: TrialOverviewWeighingWeightViewModel | TrialOverviewWeighingDateViewModel | undefined = undefined;

  const batchWeighingsForSectionAndTrial = getBatchWeighingsForSectionAndTrial(section.id, trial.id, batchWeighings);

  for (const batchWeighing of batchWeighingsForSectionAndTrial) {
    if (batchWeighing.approvals.length === 0) {
      continue;
    }

    const batch = recordGet(batches(), batchWeighing.batchId);
    console.debug(`      inspecting batchWeighing id: ${batchWeighing.id}, for section: '${sectionName}', batch: '${batch.name}', weighingType: ${batchWeighing.weighingType.type}`);
    const approvals = batchWeighing.approvals.sort(function (a: StoreApprovalStatus, b: StoreApprovalStatus) {
      return new Date(b.date).getTime() - new Date(a.date).getTime(); //descending order of the date
    });

    const latestApproval: StoreApprovalStatus | undefined = approvals.at(0); // give us the latest approval
    const status = latestApproval!.status.status;
    const weighingType = batchWeighing.weighingType.type;
    const sequenceNumber = batchWeighing.sequenceNumber;

    if (weighingType === WeighingTypeEnum.Initial) {
      initialVM = new TrialOverviewWeighingDateViewModel(
                  new ApprovalStatusType(status.toString()), latestApproval!.date);
      console.debug(`       found initial batchWeighing with approval date: ${latestApproval!.date}, id: ${batchWeighing.id}`);
    }
    else if (weighingType === WeighingTypeEnum.Interim && sequenceNumber === 1) {
      interim1VM = new TrialOverviewWeighingDateViewModel(
                  new ApprovalStatusType(status.toString()), latestApproval!.date);
      console.debug(`       found Interim1 batchWeighing with approval date: ${latestApproval!.date}, id: ${batchWeighing.id}`);
    }
    else if (weighingType === WeighingTypeEnum.Interim && sequenceNumber === 2) {
      interim2VM = new TrialOverviewWeighingDateViewModel(
                  new ApprovalStatusType(status.toString()), latestApproval!.date);
      console.debug(`       found Interim2 batchWeighing with approval date: ${latestApproval!.date}, id: ${batchWeighing.id}`);
    }
    else if (weighingType === WeighingTypeEnum.Final) {
      finalVM = new TrialOverviewWeighingDateViewModel(
                  new ApprovalStatusType(status.toString()), latestApproval!.date);
      console.debug(`       found final batchWeighing with approval date: ${latestApproval!.date}, id: ${batchWeighing.id}`);
    }
  }

  return { initialVM, interim1VM, interim2VM, finalVM };
}
